import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useWechatShare, updateWxShareData } from '../utils/wechatShare';
import { setWechatTitle } from '../utils/wechatTitle';
import { getUserLocation } from '../utils/location'
import { List, Image, PullToRefresh, SearchBar, InfiniteScroll, Ellipsis, DotLoading } from 'antd-mobile';
import FloatBackButton from '../components/FloatBackButton';
import BottomTabBar from './BottomTabBar';
import { CategoryModel } from '../models/categoryModel';
import { RegionModel } from '../models/regionModel';
import { BusinessModel } from '../models/businessModel';
import '../styles/ListPage.css';

// 常量定义
const PAGE_SIZE = 10;

const ListPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // 状态管理
  const [activeCategory, setActiveCategory] = useState(searchParams.get('category'));
  const [activeCategoryName, setActiveCategoryName] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [stableShareUrl, setStableShareUrl] = useState('');
  const [shareImgUrl, setShareImgUrl] = useState('');
  const [sortByDistanceAsc, setSortByDistanceAsc] = useState(true);
  
  // 添加用户位置状态
  const [userLocation, setUserLocation] = useState(null);
  // 流程控制状态
  const [wxInitialized, setWxInitialized] = useState(false); // 微信初始化完成标志
  const [locationFetched, setLocationFetched] = useState(false); // 位置获取完成标志
  const [dataLoaded, setDataLoaded] = useState(false); // 基础数据加载完成标志
  const [initialQueryDone, setInitialQueryDone] = useState(false); // 初始查询完成标志
  const [infiniteScrollEnabled, setInfiniteScrollEnabled] = useState(false); // 控制是否启用InfiniteScroll
  const [filtersChanged, setFiltersChanged] = useState(false); // 筛选条件是否变化
  

  // 接口数据状态
  const [categories, setCategories] = useState([]);
  const [regions, setRegions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // 分页与刷新状态
  const [businesses, setBusinesses] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  // 使用ref存储筛选条件
  const searchTermRef = useRef(searchTerm);
  const selectedRegionRef = useRef(selectedRegion);
  const userLocationRef = useRef(userLocation);
  const activeCategoryRef = useRef(activeCategory);
  const sortByDistanceAscRef = useRef(sortByDistanceAsc);

  useEffect(() => {
    activeCategoryRef.current = activeCategory;
    searchTermRef.current = searchTerm;
    selectedRegionRef.current = selectedRegion;
    userLocationRef.current = userLocation;
    sortByDistanceAscRef.current = sortByDistanceAsc;
  }, [activeCategory, searchTerm, selectedRegion, userLocation, sortByDistanceAsc]);

  // 加载商户数据
  const fetchBusinesses = useCallback(async (page = 1, isRefresh = false, categoryId = activeCategoryRef.current) => {
    console.log("fetchBusinesses....page:" + page + ",isRefresh:" + isRefresh + ",categoryId:" + categoryId)
    if (!categoryId) return;
    if (isLoadingMore && !isRefresh) return;

    // 构建查询参数，包含用户位置信息
    const queryParams = {
      categoryId: categoryId,
      name: searchTermRef.current?.length > 0 ? '*' + searchTermRef.current + '*' : undefined,
      regionId: selectedRegionRef.current,
      pageNo: page,
      pageSize: PAGE_SIZE,
      orderCol: 'distance',
      order: sortByDistanceAscRef.current ? 'asc' : 'desc',
    };

    // 如果有用户位置信息，添加到查询参数中
    if (userLocationRef.current) {
      queryParams.distanceLat = userLocationRef.current.latitude;
      queryParams.distanceLon = userLocationRef.current.longitude;
    }

    console.log(`查询参数：`, queryParams);
    
    try {
      isRefresh ? setIsRefreshing(true) : setIsLoadingMore(true);

      const rawData = await BusinessModel.getFilteredBusinesses(queryParams);
      
      const businessData = rawData?.records ?? [];

      setHasMore(businessData.length >= PAGE_SIZE);
      if (isRefresh) {
        setBusinesses(businessData);
        setCurrentPage(1);
      } else {
        setBusinesses(prev => [...prev, ...businessData]);
        setCurrentPage(page);
      }
    } catch (err) {
      console.error('商户数据加载失败', err);
      const errMsg = isRefresh ? '刷新失败，请重试' : '加载更多失败，请重试';
      setError(errMsg);
      if (isRefresh) {
          setBusinesses([]);
      }
    } finally {
      setIsRefreshing(false);
      setIsLoadingMore(false);
    }
  }, [isLoadingMore, sortByDistanceAsc]);

  // 获取分类名称
  const getCategoryName = useCallback(() => {
    const category = categories.find(c => c.id === activeCategory);
    const name = category ? category.name : '';
    setActiveCategoryName(name);
    setWechatTitle(name); // 设置页面标题
    return name;
  }, [categories, activeCategory]);

  // 配置微信分享URL（这一步应该尽早完成）
  useEffect(() => {
    const shareUrl = new URL(window.location.href);
    shareUrl.hash = '';
    Array.from(shareUrl.searchParams.keys()).forEach(key => key !== 'category' && shareUrl.searchParams.delete(key));
    setStableShareUrl(shareUrl.toString());
  }, []);

  // 使用useEffect来设置分类名称，而不是在useWechatShare中直接调用
  useEffect(() => {
    getCategoryName();
  }, [getCategoryName]);

  // 微信分享配置 - 使用useMemo稳定传递给useWechatShare的参数
  const shareTitle = useMemo(() => `${activeCategoryName || '分类'}列表`, [activeCategoryName]);
  const shareDesc = useMemo(() => `珠海${activeCategoryName || '分类'}推荐`, [activeCategoryName]);
  
  const { wx, configured } = useWechatShare(
    shareTitle,
    shareDesc,
    shareImgUrl,
    stableShareUrl
  );

  // 步骤1: 微信初始化完成后标记状态，无论是否在微信环境中
  useEffect(() => {
    // 检查是否在微信环境中
    const isWechat = /MicroMessenger/i.test(navigator.userAgent);
    
    if (isWechat && configured && wx) {
      console.log('微信环境 - 微信初始化完成');
      setWxInitialized(true);
    } else if (!isWechat) {
      console.log('非微信环境 - 跳过微信初始化');
      setWxInitialized(true); // 非微信环境直接标记微信初始化完成
    }
  }, [configured, wx]);

  // 步骤2: 微信初始化完成后，获取用户位置
  useEffect(() => {
    const initLocation = async () => {
      if (wxInitialized && !locationFetched) {
        try {
          console.log('开始获取用户位置');
          // 传递wx和configured，让getUserLocation内部自己判断是否在微信环境
          const location = await getUserLocation(wx, configured);
          console.log(`获取到用户位置: ${JSON.stringify(location)}`);
          setUserLocation(location);
        } catch (err) {
          console.error('获取位置失败:', err);
          // 位置获取失败不影响后续流程
        } finally {
          // 无论成功失败，标记位置获取完成
          setLocationFetched(true);
        }
      }
    };
    initLocation();
  }, [wxInitialized, locationFetched, wx, configured]);

  // 步骤3: 查询分类列表和区域列表
  useEffect(() => {
    const initBasicData = async () => {
      try {
        setLoading(true);
        const [categoryList, regionList] = await Promise.all([
          CategoryModel.getCategories({}),
          RegionModel.getRegions(),
        ]);
        setCategories(categoryList);
        setRegions(regionList);

        console.log('regionList--------->', regionList)
        const urlCategory = searchParams.get('category');
        const defaultCategoryId = CategoryModel.getDefaultCategoryId(categoryList, urlCategory);
        setActiveCategory(defaultCategoryId);
        
        // 标记基础数据加载完成
        setDataLoaded(true);
      } catch (err) {
        setError(err.message || '数据加载失败，请刷新重试');
        console.error('初始化基础数据失败', err);
      } finally {
        setLoading(false);
      }
    };
    initBasicData();
  }, [searchParams]);

  // 步骤4: 当位置获取完成且基础数据加载完成且有活动分类时，执行初始查询
  useEffect(() => {
    const initialQuery = async () => {
      if (locationFetched && dataLoaded && activeCategory && !initialQueryDone) {
        console.log('位置获取完成且基础数据加载完成，开始初始查询');
        await fetchBusinesses(1, true);
        setInitialQueryDone(true);
        // 初始查询完成后才启用InfiniteScroll
        setInfiniteScrollEnabled(true);
        // 重置筛选条件变更标记
        setFiltersChanged(false);
      }
    };
    initialQuery();
  }, [locationFetched, dataLoaded, activeCategory, initialQueryDone, fetchBusinesses]);


  // 下拉刷新触发
  const onRefresh = useCallback(() => {
    console.log('下拉刷新查询')
    // 下拉刷新是用户主动触发的，可以在任何时候执行
    return fetchBusinesses(1, true);
  }, [fetchBusinesses]);

  // 上拉加载触发
  const onEndReached = useCallback(() => {
    console.log("onEndReached.......", infiniteScrollEnabled, hasMore, isLoadingMore, isRefreshing)
    // 只有当InfiniteScroll启用且满足其他条件时才加载更多
    if (infiniteScrollEnabled && hasMore && !isLoadingMore && !isRefreshing) {
      console.log('上拉加载查询')
      fetchBusinesses(currentPage + 1);
    }
  }, [currentPage, hasMore, isLoadingMore, isRefreshing, fetchBusinesses, infiniteScrollEnabled]);

  // 分类切换
  const handleCategoryChange = useCallback((categoryId) => {
    // 重置筛选条件
    setActiveCategory(categoryId);
    searchParams.set('category', categoryId);
    setSearchParams(searchParams);
    setSelectedRegion('');
    setSearchTerm('');
    setSortByDistanceAsc(true);
    setBusinesses([]);
    setInitialQueryDone(false); // 重置初始查询状态，以便重新触发
    setInfiniteScrollEnabled(false); // 临时禁用InfiniteScroll
    setShareImgUrl('');
    
    // 标记筛选条件已更改，让useEffect处理查询
    setFiltersChanged(true);
  
  
    console.log('分类切换')

    // 如果位置和数据都已加载完成，立即查询
    // if (locationFetched && dataLoaded) {
    //   console.log('位置和数据都已加载完成，立即查询')
    //   fetchBusinesses(1, true, categoryId).then(() => {
    //     setInitialQueryDone(true);
    //     setInfiniteScrollEnabled(true); // 查询完成后重新启用InfiniteScroll
    //   });
    // }
  }, [locationFetched, dataLoaded, fetchBusinesses, searchParams, setSearchParams]);

  // 筛选条件变更
  const handleSearchChange = useCallback((term) => {
    setSearchTerm(term);
    setFiltersChanged(true);
  }, []);

  // 区域切换
  const toggleRegion = useCallback((region) => {
    setSelectedRegion(region);
    setFiltersChanged(true);
  }, []);

  // 通用的刷新数据函数
  const refreshBusinessData = useCallback(() => {
    // 重置状态
    setBusinesses([]);
    setCurrentPage(1);
    setHasMore(true);
    setInfiniteScrollEnabled(false); // 临时禁用InfiniteScroll
    console.log('刷新数据refreshBusinessData')
    // 执行查询
    fetchBusinesses(1, true).then(() => {
      setInfiniteScrollEnabled(true); // 查询完成后重新启用InfiniteScroll
    });
  }, [fetchBusinesses]);

  // 排序切换
  const toggleDistanceSort = useCallback(() => {
    setSortByDistanceAsc(prev => !prev);
    setFiltersChanged(true);
  
    // 添加防抖逻辑
    // const timer = setTimeout(() => {
    //   console.log('排序切换toggleDistanceSort')
    //   refreshBusinessData();
    // }, 300);
    
    // return () => clearTimeout(timer);
  }, [refreshBusinessData]);

  // 在分类变化时更新分享配置
  useEffect(() => {
    if (configured && wx) {
      // const categoryName = getCategoryName();
      console.log(`更新分享配置,\nactiveCategoryName=${activeCategoryName},\nshareImgUrl=${shareImgUrl},\nstableShareUrl=${stableShareUrl}`)
      updateWxShareData(wx, {
        title: `${activeCategoryName}列表`,
        desc: `珠海${activeCategoryName}推荐`,
        imgUrl: shareImgUrl,
        link: stableShareUrl
      });
    }
  }, [activeCategoryName, configured, wx, shareImgUrl, stableShareUrl]);

  // 当businesses数据更新且是首次设置时，设置shareImgUrl并更新微信分享配置
  useEffect(() => {
    // 只有当businesses有数据、是初始查询完成后、且还没有设置过shareImgUrl时才执行
    if (businesses.length > 0 && initialQueryDone && shareImgUrl.length === 0) {
      const newShareImgUrl = businesses[0]?.mainImageUrl ?? '';
      if (newShareImgUrl) {
        // setShareImgUrl('/images/banner2.jpg')
        setShareImgUrl(newShareImgUrl);
        console.log(`设置shareImgUrl=${newShareImgUrl}\nactiveCategoryName=${activeCategoryName}`)
        
        // 同时更新微信分享配置
        if (configured && wx && activeCategoryName) {
          updateWxShareData(wx, {
            title: `${activeCategoryName}列表`,
            desc: `珠海${activeCategoryName}推荐`,
            imgUrl: newShareImgUrl,
            link: stableShareUrl
          });
        }
      }
    }
  }, [businesses, initialQueryDone, activeCategoryName, configured, wx, stableShareUrl, shareImgUrl]);


  // 监听筛选条件变化，触发数据刷新
  useEffect(() => {
    // 只有当位置和数据都已加载完成且筛选条件有值时才触发查询
    if (locationFetched && dataLoaded && initialQueryDone 
      && filtersChanged
      // && (selectedRegion || searchTerm)
    ) {
      // 添加防抖逻辑
      const timer = setTimeout(() => {
        console.log('筛选条件变化，触发刷新')
        // 在刷新前重置filtersChanged状态，防止重复触发
        setFiltersChanged(false);
        refreshBusinessData();
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [selectedRegion, searchTerm, locationFetched, dataLoaded, initialQueryDone, filtersChanged, refreshBusinessData]);

  // 加载中/错误状态
  if (loading) {
    return <span style={{ color: '#999999', fontSize: 20 , display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', }}>
      <span>加载中</span>
      <DotLoading />
    </span>;
  }

  if (error && businesses.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-screen text-red-500">
        <p>{error}</p>
        <button
          onClick={() => fetchBusinesses(1, true)}
          className="mt-2 px-4 py-2 bg-blue-600 text-white rounded"
        >
          重试
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-f2f2f2">
      {/* 头部区域（固定） */}
      <div className="sticky top-0 z-30 bg-f2f2f2">
        {/* 筛选区域 */}
        <div className="px-4 border-b border-gray-100">
          <div className="flex items-center space-x-2 py-t-3 py-b-2">
            <div className="px-1-" ><FloatBackButton onClick={() => navigate('/')} /></div>
            {/* 区域筛选 */}
            <div className="relative w-1/3 px-2">
              <select className="w-full bg-white border-solid border border-gray-300 rounded-lg py-2 px-2"
                value={selectedRegion} onChange={(e) => toggleRegion(e.target.value)}>
                <option key={'all'} value={''}>全市</option>
                {
                  regions.map(region => (
                    <option key={region.id} value={region.id}>{region.name}</option>
                  ))
                }
              </select>
            </div>

            {/* 搜索框 */}
            <div className="relative flex-1">
              <SearchBar 
                placeholder="请输入名称"
                // value={searchTerm}
                onBlur={(e) => handleSearchChange(e.target.value)}
                onSearch={handleSearchChange}
                onClear={handleSearchChange}
                style={{'--background': 'white', '--height': '35px',}}
                // className="rounded-lg"
                // className="w-full bg-white border-solid border border-gray-300 rounded-lg py-2 px-2"
              />
            </div>
          </div>

          {/* 排序切换 */}
          <div className="mt-2 flex justify-end text-sm py-t-2 py-b-3 px-1">
            <span className="text-gray-600">位置距离：</span>
            <span
              className="flex items-center text-sm font-medium cursor-pointer"
              style={{ color: sortByDistanceAsc ? '#11AA66' : '#FFAA44' }}
              onClick={toggleDistanceSort}
            >
              {sortByDistanceAsc ? '升序' : '降序'}
              <img
                src={sortByDistanceAsc ? '/images/sort_up.png' : '/images/sort_down.png'}
                className="ml-1 w-4 h-4 object-contain"
                style={{ verticalAlign: 'middle' }}
              />
            </span>
          </div>
        </div>
      </div>

      {/* 核心列表区域（下拉刷新 + 上拉加载） */}
      <div className="flex-1" style={{ paddingBottom: 70 }}>
        <PullToRefresh onRefresh={onRefresh} refreshing={isRefreshing} >
          <List
            itemLayout="vertical"
            style={{
              "--align-items": "start"
            }}
          >
            {businesses.map(business => (
              <List.Item key={business.id} 
                prefix={
                  <Image
                    src={business.mainImageUrl || '/images/default_main_img.jpg'}
                    width={130}
                    height={130}
                    fit='cover'
                    style={{ borderRadius: '1rem' }}
                  />
                }
                description={<Ellipsis direction='end' rows={4} content={business.description} />}
                arrowIcon={false}
                onClick={() => navigate(`/detail/${business.id}`)}
              >
                <div className="list-item-title">{business.name}</div>
              </List.Item>
            ))}
          </List>
          {/* 只有当infiniteScrollEnabled为true时才渲染InfiniteScroll组件 */}
          {infiniteScrollEnabled && (
            <InfiniteScroll loadMore={onEndReached} hasMore={hasMore} />
          )}
        </PullToRefresh>
      </div>

      {/* 底部固定 Tabs */}
      <div><BottomTabBar activeCategory={activeCategory} onTabChange={handleCategoryChange} /></div>
    </div>
  );
};

export default ListPage;